import React, {useEffect} from "react";
import {
    Grid,
    FormControlLabel,
    Switch,
    TextField
} from "@material-ui/core";
import { useStyles } from "./styles";
import clsx from "clsx";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChartSimple as countsIcon } from "@fortawesome/pro-light-svg-icons";
import { ROUTINE_TYPE } from "../../../../constants/index";
import { useForm, Controller } from "react-hook-form";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import ActionForm from "./ActionForm";

export const OccupancyCountAddEditSchema = Yup.object().shape({
    ContractCountsTo: Yup.number()
        .typeError("Value must be a number")
        .min(0, "Value must be 0 or greater")
        .max(9999999, "Value must be 9,999,999 or less")
        .integer("Only whole numbers are allowed")
        .required("Required"),
    TransientCountsTo: Yup.number()
        .typeError("Value must be a number")
        .min(0, "Value must be 0 or greater")
        .max(9999999, "Value must be 9,999,999 or less")
        .integer("Only whole numbers are allowed")
        .required("Required"),
    FacilityCountsTo: Yup.number()
        .typeError("Value must be a number")
        .min(0, "Value must be 0 or greater")
        .max(9999999, "Value must be 9,999,999 or less")
        .integer("Only whole numbers are allowed")
        .required("Required")
});

const SetOccupancyCountsActionForm = ({ handleSave, handleCancel, action }) => {
    const classes = useStyles();

    let defaults = {
        ContractEnabled: false,
        ContractCountsTo: 0,
        TransientEnabled: false,
        TransientCountsTo: 0,
        FacilityEnabled: false,
        FacilityCountsTo: 0
    };

    const formatAndSave = (values) => {
        var template = {
            payload: {
                ContractEnabled: values.ContractEnabled,
                ContractCountsTo: values.ContractCountsTo,
                TransientEnabled: values.TransientEnabled,
                TransientCountsTo: values.TransientCountsTo,
                FacilityEnabled: values.FacilityEnabled,
                FacilityCountsTo: values.FacilityCountsTo
            },
            actionTypeID: ROUTINE_TYPE.SET_OCCUPANCY_COUNT,
            order: action?.order ?? 0,
        };
        handleSave(template);
    };

    const {
        handleSubmit,
        control,
        reset,
        formState: { errors },
        watch,
    } = useForm({
        defaultValues: defaults,
        resolver: yupResolver(OccupancyCountAddEditSchema),
        mode: "onChange"
    });
    const watchFields = watch();

    useEffect(() => {
        if (action) reset(action?.payload);
    }, [action]);

    return (
        <>
            <ActionForm
                handleSave={formatAndSave}
                handleSubmit={handleSubmit}
                handleCancel={handleCancel}
                icon={<FontAwesomeIcon icon={countsIcon} />}
                edit={action}
                primaryText={"Counts"}
                secondaryText={"Set Occupancy Counts"}
            >
                <Grid container className={clsx([classes.drawerContainer])}>
                    <Grid item xs={12}>
                        <Controller
                            name="ContractEnabled"
                            control={control}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            {...field}
                                            id="ContractEnabled"
                                            color="primary"
                                            data-testid="Contractswitch"
                                            onChange={(e) => {
                                                field.onChange(e.target.checked);
                                            }}
                                            checked={field.value}
                                            data-checked={field.value}
                                        />}
                                    label="Contract"
                                />
                            )}
                        />
                        <Controller
                            name="ContractCountsTo"
                            control={control}
                            render={({ field }) => (
                                <Grid container>
                                    <Grid item xs={12} sm={8}>
                                        <TextField
                                            {...field}
                                            disabled={!watchFields.ContractEnabled}
                                            data-id={"ContractCountsTo"}
                                            data-testid={"ContractCountsTo"}
                                            variant="outlined"
                                            margin="normal"
                                            label="Set Contract Counts To"
                                            required
                                            fullWidth
                                            id="ContractCountsTo"
                                            error={!!errors.ContractCountsTo}
                                            helperText={(errors.ContractCountsTo && errors.ContractCountsTo.message) ||
                                                ""}
                                        />
                                    </Grid>
                                </Grid>
                            )}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Controller
                            name="TransientEnabled"
                            control={control}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            {...field}
                                            id="TransientEnabled"
                                            color="primary"
                                            data-testid="Transientswitch"
                                            onChange={(e) => {
                                                field.onChange(e.target.checked);
                                            }}
                                            checked={field.value}
                                            data-checked={field.value}
                                        />}
                                    label="Transient"
                                />
                            )}
                        />
                        <Controller
                            name="TransientCountsTo"
                            control={control}
                            render={({ field }) => (
                                <Grid container>
                                    <Grid item xs={12} sm={8}>
                                        <TextField
                                            {...field}
                                            disabled={!watchFields.TransientEnabled}
                                            data-id={"TransientCountsTo"}
                                            data-testid={"TransientCountsTo"}
                                            variant="outlined"
                                            margin="normal"
                                            label="Set Transient Counts To"
                                            required
                                            fullWidth
                                            id="TransientCountsTo"
                                            error={!!errors.TransientCountsTo}
                                            helperText={(errors.TransientCountsTo && errors.TransientCountsTo.message) ||
                                                ""}
                                        />
                                    </Grid>
                                </Grid>
                            )}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Controller
                            name="FacilityEnabled"
                            control={control}
                            render={({ field }) => (
                                <FormControlLabel
                                    control={
                                        <Switch
                                            {...field}
                                            id="FacilityEnabled"
                                            color="primary"
                                            data-testid="Facilityswitch"
                                            onChange={(e) => {
                                                field.onChange(e.target.checked);
                                            }}
                                            checked={field.value}
                                            data-checked={field.value}
                                        />}
                                    label="Facility"
                                />
                            )}
                        />
                        <Controller
                            name="FacilityCountsTo"
                            control={control}
                            render={({ field }) => (
                                <Grid container>

                                    <Grid item xs={12} sm={8}>
                                        <TextField
                                            {...field}
                                            disabled={!watchFields.FacilityEnabled}
                                            data-id={"FacilityCountsTo"}
                                            data-testid={"FacilityCountsTo"}
                                            variant="outlined"
                                            margin="normal"
                                            label="Set Facility Counts To"
                                            required
                                            fullWidth
                                            id="FacilityCountsTo"
                                            error={!!errors.FacilityCountsTo}
                                            helperText={(errors.FacilityCountsTo && errors.FacilityCountsTo.message) ||
                                                ""}
                                        />
                                    </Grid>
                                </Grid>
                            )}
                        />
                    </Grid>
                </Grid>
            </ActionForm>
        </>
    );
};
export default SetOccupancyCountsActionForm;